// The live graph's state, as a pure reducer over the contract's shapes:
// GET /rail/graph (RailGraph: LiveNode, LiveEdge) and /rail/events (LiveEvent,
// rail-events.asyncapi.yaml). It reads only what the contract gives a node: DID,
// type, standing (a label) and rung. Claims are satellites of their holder,
// known from edges' claim_id and from claim messages.
import type { components } from "../api/schema";
import type { LiveEvent } from "../api/events";

export type LiveNode = components["schemas"]["LiveNode"];
export type LiveEdge = components["schemas"]["LiveEdge"];
export type RailGraph = components["schemas"]["RailGraph"];
export type Standing = LiveNode["standing"];
export type NodeType = LiveNode["type"];
export type EdgeKind = LiveEdge["kind"];

export interface Pulse { edgeId: string; claimId: string; issuer: string; at: number }

export interface GraphState {
  nodes: Map<string, LiveNode>;
  edges: Map<string, LiveEdge>;
  claims: Map<string, string>;          // claim_id -> holder DID
  asOfSeq: number;                      // the snapshot's last seq; entry-backed messages at or below it are already in
  lastSeq: number;
  mode: "live" | "replay";
  replaying: boolean;                   // a replay message arrived recently
  pulses: Pulse[];
  version: number;                      // bumps on every change, for renderers
}

export const PULSE_MS = 4000;

export function empty(mode: GraphState["mode"] = "live"): GraphState {
  return { nodes: new Map(), edges: new Map(), claims: new Map(), asOfSeq: -1, lastSeq: -1, mode, replaying: false,
           pulses: [], version: 0 };
}

export function applySnapshot(g: GraphState, snap: RailGraph): GraphState {
  const nodes = new Map(snap.nodes.map((n) => [n.did, n]));
  const edges = new Map(snap.edges.map((e) => [e.id, e]));
  const claims = new Map<string, string>();
  for (const e of snap.edges) claims.set(e.claim_id, e.to);
  return { ...g, nodes, edges, claims, asOfSeq: snap.as_of_seq, lastSeq: Math.max(g.lastSeq, snap.as_of_seq), version: g.version + 1 };
}

/** Apply one stream message. `now` stamps pulses (an anchor that is new to this client). */
export function applyEvent(g: GraphState, ev: LiveEvent, now: number): GraphState {
  // Already in the snapshot: an entry-backed live message at or below its seq (replays always apply).
  if (!ev.replay && typeof ev.seq === "number" && ev.seq <= g.asOfSeq) return g;
  const nodes = new Map(g.nodes), edges = new Map(g.edges), claims = new Map(g.claims);
  for (const n of ev.nodes) nodes.set(n.did, n);
  const pulses = g.pulses.filter((p) => now - p.at < PULSE_MS);
  // Removals first: a demotion removes the anchor edge and adds the affiliated edge under the same id.
  for (const e of ev.removed_edges ?? []) edges.delete(e.id);
  for (const e of ev.edges) {
    const before = edges.get(e.id);
    edges.set(e.id, e);
    claims.set(e.claim_id, e.to);
    if (e.kind === "anchor" && before?.kind !== "anchor") pulses.push({ edgeId: e.id, claimId: e.claim_id, issuer: e.from, at: now });
  }
  if (ev.claim) claims.set(ev.claim.claim_id, ev.claim.holder);
  return { ...g, nodes, edges, claims, pulses, lastSeq: typeof ev.seq === "number" ? Math.max(g.lastSeq, ev.seq) : g.lastSeq,
           replaying: ev.replay, version: g.version + 1 };
}

export const activePulses = (g: GraphState, now: number) => g.pulses.filter((p) => now - p.at < PULSE_MS);

/** One DID and what touches it: its claims, the edges into them and the DIDs at their other ends (a gig worker's job fan). */
export function focus(g: GraphState, did: string): { nodes: LiveNode[]; edges: LiveEdge[]; claims: [string, string][] } {
  const edges = [...g.edges.values()].filter((e) => e.to === did || e.from === did || e.attester_did === did);
  // A delegated anchor brings its reviewer (attester_did) into the fan, as the issuer.
  const dids = new Set<string>([did, ...edges.flatMap((e) => [e.from, e.to, ...(e.attester_did ? [e.attester_did] : [])])]);
  const claimIds = new Set([...[...g.claims].filter(([, h]) => h === did).map(([c]) => c), ...edges.map((e) => e.claim_id)]);
  return { nodes: [...dids].map((d) => g.nodes.get(d)).filter((n): n is LiveNode => !!n), edges,
           claims: [...claimIds].map((c) => [c, g.claims.get(c)!] as [string, string]).filter(([, h]) => !!h) };
}

// Encodings. Size follows the standing label; colour the node type; thickness the edge kind.
// Typed as total records over the contract's enums, so a new enum value fails the build
// until it has an encoding.
// The standing band (#89): new, established, strong; compromised is drawn small with a red ring.
export const RADIUS: Record<Standing, number> = { new: 5, established: 7.5, strong: 10, compromised: 5 };
export const COLOUR: Record<NodeType, string> = { peer: "var(--peer)", affiliated: "var(--affiliated)",
  light_issuer: "var(--light-issuer)", issuer: "var(--issuer)" };
export const THICKNESS: Record<EdgeKind, number> = { peer: 1, affiliated: 1.6, organisation: 2.4, anchor: 3.4 };
export const TYPE_LABEL: Record<NodeType, string> = { peer: "Peer", affiliated: "Affiliated", light_issuer: "Light issuer", issuer: "Full issuer" };
